// Resolving the manual selections to apply when (re)initialising the stem-selection
// panel: either mirror the currently selected stem, or reset and default the head
// offset for the system being switched to. No API access and no Vue reactivity.
import type { CatalogHead } from '@/domain/catalog/head';
import {
    type CatalogStem,
    formatStemSystem,
    type StemOffset,
    type StemSize,
    stemSizeFromString,
    type StemSystem,
} from '@/domain/catalog/stem';
import { getCatalogComponent } from '@/domain/catalog/catalog';
import type { Url } from '@/domain/types';
import { formatNumberSign } from '@/util/format/formatNumberSign';
import type {
    SelectableHeadOffset,
    SelectableSystemProfile,
    StemSystemToggle,
} from '../stem.types';
import { getSelectableHeadOffsetsForSystem } from './stemHeadOffsets';

export type ManualSelectionInputs = {
    catalogStems: Map<string, CatalogStem>;
    catalogHeads: Map<string, CatalogHead>;
    templateStemUrl: Url;
    templateHeadUrl: Url;
    preferredSystem: StemSystem | null;
    selectedStemSystem: StemSystemToggle;
    selectedCupSize: number | null;
    dualMobility: boolean;
};

export type ManualSelectionResult =
    | {
          mode: 'matched';
          systemProfile: SelectableSystemProfile;
          offset: StemOffset;
          size: StemSize;
          headOffset: SelectableHeadOffset;
      }
    | { mode: 'switched'; headOffset: SelectableHeadOffset | null };

const isStemMatchingToggle = (
    stemSystem: StemSystem,
    preferredSystem: StemSystem | null,
    selectedStemSystem: StemSystemToggle,
): boolean =>
    selectedStemSystem === null ||
    (stemSystem === preferredSystem && selectedStemSystem === 'preferred') ||
    (stemSystem !== preferredSystem && selectedStemSystem === 'alternative');

const targetSystemForToggle = (
    catalogStems: Map<string, CatalogStem>,
    preferredSystem: StemSystem | null,
    selectedStemSystem: StemSystemToggle,
): StemSystem | null => {
    if (selectedStemSystem === 'preferred') {
        return preferredSystem;
    }
    if (selectedStemSystem === 'alternative' && preferredSystem) {
        // Note: only resolves the first alternative system; multiple alternatives are unsupported.
        return (
            Array.from(catalogStems.values()).find((stem) => stem.system !== preferredSystem)
                ?.system ?? null
        );
    }
    return null;
};

// Resolve the manual selections to apply when (re)initialising the panel: either
// mirror the currently selected stem ('matched'), or reset and default the head
// offset for the system being switched to ('switched').
export const resolveManualSelection = (inputs: ManualSelectionInputs): ManualSelectionResult => {
    const catalogStem = getCatalogComponent(inputs.catalogStems, inputs.templateStemUrl);
    const catalogHead = getCatalogComponent(inputs.catalogHeads, inputs.templateHeadUrl);

    if (
        isStemMatchingToggle(catalogStem.system, inputs.preferredSystem, inputs.selectedStemSystem)
    ) {
        return {
            mode: 'matched',
            systemProfile: {
                name: `${formatStemSystem(catalogStem.system)} - ${catalogStem.profile}`,
                system: catalogStem.system,
                profile: catalogStem.profile,
            },
            offset: catalogStem.offset,
            size: stemSizeFromString(catalogStem.size),
            headOffset: { text: formatNumberSign(catalogHead.offset), headUrl: catalogHead.self },
        };
    }

    const targetSystem = targetSystemForToggle(
        inputs.catalogStems,
        inputs.preferredSystem,
        inputs.selectedStemSystem,
    );
    if (!targetSystem) {
        return { mode: 'switched', headOffset: null };
    }
    const headOffsets = getSelectableHeadOffsetsForSystem({
        heads: inputs.catalogHeads,
        stemSystem: targetSystem,
        headSize: catalogHead.size,
        selectedCupSize: inputs.selectedCupSize,
        dualMobility: inputs.dualMobility,
    });
    // Default to the 0 offset, falling back to the first available.
    return {
        mode: 'switched',
        headOffset: headOffsets.find((h) => h.text === '0') ?? headOffsets[0] ?? null,
    };
};